import { isFooterRow } from './../../../../Simulation/helpers/gridHelpers';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import {
    LAST_YEAR_COLUMN_STYLE,
    GREY_HEADER_CLASS,
} from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/gridHelpers';
import { DEFAULT_AMOUNT_COLUMN } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/defaultColDefs';
import { getAgentColumn } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/agentColumnHelpers';
import {
    COMP_HOURS_COST,
    EXTRA_HOURS_COST,
    COMP_HOURS_COUNT,
    EXTRA_HOURS_COUNT,
    TOTAL_HOURS_COST,
    TOTAL_HOURS_COUNT,
    ASTREINTE_HOURS_COST,
    PRIMES_COSTS,
} from 'routes/HumanResourcesModule/business/definitions';
import { getGroupedOrgChartHrColumn } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/orgChartHrColumnHelpers';
import { floatFormatter } from 'ui/AgGridReact/formatters';
import { getMantyStatusColumn } from 'routes/HumanResourcesModule/PayState/helpers/colDefinitions';

const displayedComponent = [
    EXTRA_HOURS_COST,
    EXTRA_HOURS_COUNT,
    COMP_HOURS_COST,
    COMP_HOURS_COUNT,
    TOTAL_HOURS_COST,
    TOTAL_HOURS_COUNT,
    ASTREINTE_HOURS_COST,
    PRIMES_COSTS,
];

const getAmountColumns = ({
    exercice,
    month,
    showLastYearColumns,
    t,
}: {
    exercice: number;
    month: number;
    showLastYearColumns: boolean;
    t: TranslationT;
}): ColDef[] => {
    const displayedMonth = month === 0 ? 1 : month;
    return displayedComponent.flatMap((comp) => {
        const paddedMonth = String(displayedMonth).padStart(2, '0');
        const previousYearSuffix = `${exercice - 1}${paddedMonth}01`;
        const currentYearSuffix = `${exercice}${paddedMonth}01`;
        const columnTitle = (col: string) =>
            t(`humanResources.payState.${col}`);

        const isCost = comp.includes('Cost');
        const compType = comp.replace(/(Cost|Count)$/, '');

        let tooltip = '';
        if (comp === TOTAL_HOURS_COST || comp === TOTAL_HOURS_COUNT) {
            tooltip = t(
                `humanResources.payState.variableElementsTooltips.${comp}`,
            );
        } else if (isCost) {
            tooltip = t(
                'humanResources.payState.variableElementsTooltips.description',
                {
                    hoursType: t(
                        `humanResources.payState.${compType}`,
                    ).toLowerCase(),
                },
            );
        }

        const defaultValue = isCost
            ? {
                  ...DEFAULT_AMOUNT_COLUMN,
                  headerTooltip: tooltip,
              }
            : {
                  aggFunc: 'sum',
                  valueFormatter: floatFormatter,
                  headerTooltip: tooltip,
              };

        return [
            {
                ...defaultValue,
                headerName: `${columnTitle(comp)} ${exercice - 1}`,
                headerTooltip: comp,
                hide: !showLastYearColumns,
                field: `${comp}..${previousYearSuffix}`,
                cellStyle: LAST_YEAR_COLUMN_STYLE,
                headerClass: GREY_HEADER_CLASS,
                cellRendererSelector: (params: ICellRendererParams) =>
                    params.node.rowPinned
                        ? { component: 'bottomPinnedRowRenderer' }
                        : undefined,
            },
            {
                ...defaultValue,
                headerName: columnTitle(comp),
                field: `${comp}..${currentYearSuffix}`,
                cellRendererSelector: (params: ICellRendererParams) => {
                    if (params.node.rowPinned) {
                        return { component: 'bottomPinnedRowRenderer' };
                    }
                    if (isFooterRow(params)) {
                        return { component: undefined };
                    }
                    return {
                        component: 'lastYearComparatorNumberCellRenderer',
                    };
                },
                cellRendererParams: { exercice },
            },
        ];
    });
};

export const getColDefs = ({
    exercice,
    month,
    showLastYearColumns,
    t,
}: {
    exercice: number;
    month: number;
    showLastYearColumns: boolean;
    t: TranslationT;
}): ColDef[] => [
    getGroupedOrgChartHrColumn(),
    getAgentColumn(),
    getMantyStatusColumn(),
    ...getAmountColumns({ exercice, month, showLastYearColumns, t }),
];
